import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, DatePicker, Form, Input, Row, Select, Space } from 'antd';
import {
  getSubsidiaries,
  setSubsidiary,
  getCategories,
  getProviders,
  getCabainTypes,
  getAllRoles,
  getAllDepartments,
  getAllJobPositions,
  getTreatmentTypes,
  getCabains,
  getWeeksOnlyYear,
} from '../../../appRedux/actions';
import { CustomSelect } from '../CustomSelect';
import {
  payConditionsConst,
  payMethods,
  payStatus,
  roleConst,
  orderTypes,
  productTypes,
  productStatus,
  buttonLabel,
  formConfig,
  orderStatus,
  placeHolders,
  genders,
  maritalStatus,
  statusDescriptions,
  types,
  customerConst,
  couponTypeConst,
  loyaltyScaleConst,
} from '../../../constants';
import {
  dateFormat,
  monthName,
  monthTwoDigit,
  yearFourDigit,
} from '../../../constants/TimeConst';
import moment from 'moment';
import { categoryTypeConst } from '../../../constants/Category.const';
import { getModules } from '../../../appRedux/actions/Module';
import { actionsConst } from '../../../constants/Logs.const';
import { dateStatus } from '../../../constants/Diary.const';
import {
  paymentMethod,
  paymentGatewayOptions,
  paymentStatusOptions,
  treatmentStatusOptions,
} from '../../../constants/Sales';
import {
  setFiltersData,
  clearFiltersData,
  clearPaginationConfig,
  setFormFilters,
  setPreviousPath,
  clearFormFilters,
} from '../../../appRedux/actions/General';
import { useLocation } from 'react-router-dom';
import { stringDate } from '../../../util';

export const TableFilters = ({
  privileges = [],
  filters,
  searchFilters,
  searchInputInitialValues,
  role,
  initialParams = {},
  wrap = false,
  justify = 'end',
  paymentMethodOptions = paymentMethod,
  paymentGatewayOptions: gatewayOptions = paymentGatewayOptions,
  paymentGatewayPlaceholder = placeHolders.paymentGateway,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { subsidiaries } = useSelector((state) => state.subsidiary);
  const { categories } = useSelector((state) => state.category);
  const { providers } = useSelector((state) => state.provider);
  const { fortnightlysWeeks } = useSelector((state) => state.fortnightlys);
  const { cabainTypes } = useSelector((state) => state.cabainType);
  const { cabains } = useSelector((state) => state.cabain);
  const { roles } = useSelector((state) => state.role);
  const { departments } = useSelector((state) => state.departments);
  const { jobPositions } = useSelector((state) => state.jobPositions);
  const { modules } = useSelector((state) => state.modules);
  const { treatmentTypes } = useSelector((state) => state.treatmentType);
  const { formFilters, previousPath } = useSelector(
    (state) => state?.generalStates
  );
  const [form] = Form.useForm();
  const [branchId, setBranchId] = useState('');
  const { RangePicker } = DatePicker;
  const currentPath = location.pathname;
  const [currentLocation] = currentPath.split('/').slice(2);

  const [fortnightlyYearValue, setFortnightlyYearValue] = useState('');

  useEffect(() => {
    if (previousPath && previousPath.includes(currentLocation)) return;

    form.resetFields();
    dispatch(clearFormFilters());
    dispatch(clearFiltersData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, form, location.pathname]);

  const onFortnightlyYearChange = async (e) => {
    const value = e.target.value;
    setFortnightlyYearValue(value);

    form.setFieldsValue({ fortnightlyWeekRange: undefined });

    if (value && value.length === 4 && /^\d{4}$/.test(value)) {
      await dispatch(getWeeksOnlyYear(value));
    }
  };

  const handleClearFilters = useCallback(() => {
    form.resetFields();
    form.setFieldsValue({ status: undefined });
    dispatch(setFiltersData({}));
    dispatch(clearFiltersData());
    dispatch(clearPaginationConfig());
    dispatch(clearFormFilters({}));
  }, [dispatch, form]);

  useEffect(() => {
    if (previousPath && previousPath.includes(currentLocation)) {
      form.setFieldsValue(formFilters);
    }

    dispatch(setPreviousPath(''));
  }, [form, formFilters, currentLocation, previousPath, dispatch]);

  useEffect(() => {
    if (
      filters.branches ||
      filters.transferBranches ||
      filters.redemptionBranch
    ) {
      dispatch(getSubsidiaries({ status: 1 }));
    }
  }, [
    dispatch,
    filters.branches,
    filters.transferBranches,
    filters.redemptionBranch,
  ]);

  useEffect(() => {
    if (filters.categories) {
      let categoryType = '';
      if (filters.treatmentCategory) {
        categoryType = 'treatment';
      } else if (filters.productsCategories) {
        categoryType = 'product';
      }
      dispatch(getCategories({ type: categoryType }));
    }
  }, [
    dispatch,
    filters.categories,
    filters.treatmentCategory,
    filters.productsCategories,
  ]);

  useEffect(() => {
    if (filters.cabainTypes) dispatch(getCabainTypes());
  }, [dispatch, filters.cabainTypes]);

  useEffect(() => {
    if (filters.cabains || filters.multipleCabains) dispatch(getCabains());
  }, [dispatch, filters.cabains, filters.multipleCabains]);

  useEffect(() => {
    if (filters.providers) dispatch(getProviders());
  }, [dispatch, filters.providers]);

  useEffect(() => {
    if (filters.treatmentType) dispatch(getTreatmentTypes());
  }, [dispatch, filters.treatmentType]);

  useEffect(() => {
    if (filters.modules) dispatch(getModules());
  }, [dispatch, filters.modules]);

  useEffect(() => {
    dispatch(setSubsidiary(branchId));
  }, [dispatch, branchId]);

  useEffect(() => {
    if (filters.roles) dispatch(getAllRoles());
  }, [dispatch, filters.roles]);

  useEffect(() => {
    if (filters.departments) dispatch(getAllDepartments());
  }, [dispatch, filters.departments]);

  useEffect(() => {
    if (filters.jobPositions) dispatch(getAllJobPositions());
  }, [dispatch, filters.jobPositions]);

  useEffect(() => {
    if (initialParams.branchId)
      form.setFieldsValue({ branchesIds: [initialParams.branchId] });
  }, [initialParams.branchId, form]);

  const onBranchChange = (branchId) => {
    setBranchId(branchId);
  };

  const buildFortnightOptions = (weeks = []) => {
    const map = new Map();

    weeks.forEach((w) => {
      const fortnight = String(w.number);
      const start = w?.startDate || w?.start_date;
      const end = w?.endDate || w?.end_date;

      if (!map.has(fortnight)) {
        map.set(fortnight, { start, end });
        return;
      }

      const current = map.get(fortnight);
      const startDate = new Date(start);
      const endDate = new Date(end);

      const currentStart = new Date(current.start);
      const currentEnd = new Date(current.end);

      if (startDate < currentStart) current.start = start;
      if (endDate > currentEnd) current.end = end;
    });

    return Array.from(map.entries())
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([fortnight, range]) => ({
        value: `${fortnight}`,
        label: `${fortnight}: ${stringDate(range.start)} - ${stringDate(
          range.end
        )}`,
      }));
  };

  const onSubmit = (values) => {
    dispatch(clearFormFilters());
    dispatch(setFormFilters(values));
    dispatch(clearPaginationConfig());

    if (values.dates)
      values.dates = values.dates.map((date) =>
        moment(date)?.format(dateFormat)
      );

    if (values.year) {
      values.year = moment(values.year).year();
    }

    if (values.month) {
      values.month = moment(values.month)?.format(monthTwoDigit);
    }

    if (values.branchesIds) {
      dispatch(getCabains({ branchesIds: values.branchesIds }));
    }

    dispatch(clearFiltersData());
    dispatch(setFiltersData(values));
  };

  return (
    <Row justify={justify} style={wrap ? { width: '100%' } : undefined}>
      <Form
        {...formConfig}
        style={wrap ? { width: '100%' } : undefined}
        onFinish={onSubmit}
        form={form}
        initialValues={{
          ...(filters.status ? { status: '1' } : {}),
          ...searchInputInitialValues,
        }}
      >
        <Space
          wrap={wrap}
          size={wrap ? [8, 8] : undefined}
          style={
            wrap
              ? { display: 'flex', justifyContent: justify, width: '100%' }
              : undefined
          }
        >
          {filters.loyaltyScale && (
            <Form.Item name="loyaltyScale">
              <CustomSelect
                placeholder={placeHolders.loyaltyScale}
                style={{ width: '150px' }}
                options={loyaltyScaleConst}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.dates && (
            <Form.Item name="dates">
              <RangePicker format={dateFormat} />
            </Form.Item>
          )}

          {filters.year && (
            <Form.Item name="year">
              <DatePicker
                picker="year"
                format={yearFourDigit}
                placeholder={placeHolders.year}
              />
            </Form.Item>
          )}

          {filters.month && (
            <Form.Item name="month">
              <DatePicker
                picker="month"
                format={monthName}
                placeholder={placeHolders.month}
              />
            </Form.Item>
          )}

          {filters.categories && (
            <Form.Item name="categoriesIds">
              <CustomSelect
                mode="multiple"
                placeholder={placeHolders.filterCategories}
                style={{ width: '150px' }}
                options={categories?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.branches && (
            <Form.Item
              name="branchesIds"
              hidden={!privileges?.includes('approve')}
            >
              <CustomSelect
                mode="multiple"
                placeholder={placeHolders.branch}
                style={{ minWidth: '200px' }}
                onChange={onBranchChange}
                options={subsidiaries?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.multipleCabains && (
            <Form.Item name="cabainsIds">
              <CustomSelect
                mode="multiple"
                placeholder={placeHolders.cabain}
                style={{ minWidth: '200px' }}
                options={cabains?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.jobPositions && (
            <Form.Item name="jobPositionId">
              <CustomSelect
                placeholder={placeHolders.filterJobPosition}
                style={{ width: '150px' }}
                options={jobPositions?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}
          {filters.departments && (
            <Form.Item name="departmentId">
              <CustomSelect
                placeholder={placeHolders.filterDepartment}
                style={{ width: '150px' }}
                options={departments?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}
          {filters.roles && (
            <Form.Item name="roleId">
              <CustomSelect
                placeholder={placeHolders.filterRole}
                style={{ width: '150px' }}
                options={roles?.map(({ id, role }) => ({
                  value: id,
                  label: role,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.fortnightly && (
            <>
              <Form.Item name="year">
                <Input
                  placeholder={placeHolders.typeYear}
                  style={{ width: '150px' }}
                  value={fortnightlyYearValue}
                  onChange={onFortnightlyYearChange}
                  maxLength={4}
                />
              </Form.Item>

              <Form.Item name="fourteenDays">
                <Select
                  placeholder={placeHolders.fortnightly}
                  style={{ width: '350px' }}
                  disabled={!fortnightlyYearValue}
                  options={buildFortnightOptions(fortnightlysWeeks)}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </>
          )}

          {filters.transferBranches && (
            <>
              <Form.Item
                name="originBranchId"
                hidden={
                  !privileges?.includes('approve') &&
                  !privileges?.includes('reports')
                }
              >
                <CustomSelect
                  hidden={role === roleConst.branchManager}
                  placeholder={placeHolders.originBranch}
                  style={{ minWidth: '150px' }}
                  onChange={onBranchChange}
                  options={subsidiaries?.map(({ id, name }) => ({
                    value: id,
                    label: name,
                  }))}
                ></CustomSelect>
              </Form.Item>

              <Form.Item
                name="destinyBranchId"
                hidden={
                  !privileges.includes('approve') &&
                  !privileges.includes('reports')
                }
              >
                <CustomSelect
                  placeholder={placeHolders.destinyBranch}
                  style={{ minWidth: '150px' }}
                  onChange={onBranchChange}
                  options={subsidiaries?.map(({ id, name }) => ({
                    value: id,
                    label: name,
                  }))}
                ></CustomSelect>
              </Form.Item>
            </>
          )}

          {filters.applianceType && (
            <Form.Item name="applianceType">
              <CustomSelect
                placeholder={placeHolders.applianceType}
                style={{ width: '130px' }}
                options={types}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.treatmentType && (
            <Form.Item name="treatmentTypeId">
              <CustomSelect
                placeholder={placeHolders.filterTreatmentType}
                style={{ width: '150px' }}
                options={treatmentTypes?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.couponType && (
            <Form.Item name="couponType">
              <CustomSelect
                placeholder={placeHolders.couponType}
                style={{ width: '150px' }}
                options={couponTypeConst?.map(({ value, label }) => ({
                  value,
                  label,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.modules && (
            <Form.Item name="moduleId">
              <CustomSelect
                placeholder={placeHolders.modules}
                style={{ width: '150px' }}
                options={modules?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.actions && (
            <Form.Item name="action">
              <CustomSelect
                placeholder={placeHolders.action}
                style={{ width: '150px' }}
                options={actionsConst}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.franchise && (
            <Form.Item name="franchise">
              <CustomSelect
                placeholder={placeHolders.franchise}
                style={{ width: '150px' }}
                options={[
                  { value: '1', label: 'Si' },
                  { value: '0', label: 'No' },
                ]}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.productTypes && (
            <Form.Item name="productType">
              <CustomSelect
                placeholder={placeHolders.filterType}
                style={{ width: '150px' }}
                options={productTypes}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.cabainTypes && (
            <Form.Item name="cabainType">
              <CustomSelect
                placeholder={placeHolders.cabainType}
                style={{ width: '150px' }}
                options={cabainTypes?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.cabains && (
            <Form.Item name="cabainId">
              <CustomSelect
                placeholder={placeHolders.filterCabain}
                style={{ width: '150px' }}
                options={cabains?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.gender && (
            <Form.Item name="gender">
              <CustomSelect
                placeholder={placeHolders.filterGender}
                style={{ width: '130px' }}
                options={genders}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.maritalStatus && (
            <Form.Item name="maritalStatus">
              <CustomSelect
                placeholder={placeHolders.filterMaritalStatus}
                style={{ width: '130px' }}
                options={maritalStatus}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.status && (
            <Form.Item name="status">
              <Select
                allowClear
                placeholder={placeHolders.filterStatus}
                style={{ width: '100px' }}
                options={[
                  { label: 'Activo', value: '1' },
                  { label: 'Inactivo', value: '0' },
                ]}
              ></Select>
            </Form.Item>
          )}
          {filters.pendingStatus && (
            <Form.Item name="status">
              <Select
                allowClear
                placeholder={placeHolders.filterStatus}
                style={{ width: '100px' }}
                options={[
                  { label: 'Pendiente', value: 'Pendiente' },
                  { label: 'Aprobada', value: 'Aprobada' },
                ]}
              ></Select>
            </Form.Item>
          )}

          {filters.appointmentStatus && (
            <Form.Item name="appointmentStatus">
              <CustomSelect
                placeholder={placeHolders.appointmentStatus}
                style={{ width: '100px' }}
                options={dateStatus}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.blackList && (
            <Form.Item name="blackList">
              <CustomSelect
                placeholder={placeHolders.blackList}
                style={{ width: '120px' }}
                options={[
                  customerConst.blackList,
                  customerConst.regularCustomer,
                ]}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.productStatus && (
            <Form.Item name="productStatus">
              <CustomSelect
                placeholder={placeHolders.state}
                style={{ width: '150px' }}
                options={productStatus}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.applianceStatus && (
            <Form.Item name="applianceStatus">
              <CustomSelect
                placeholder={placeHolders.state}
                style={{ width: '130px' }}
                options={statusDescriptions}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.orderType && (
            <Form.Item name="orderType">
              <CustomSelect
                placeholder={placeHolders.filterType}
                style={{ width: '200px' }}
                options={orderTypes}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.orderStatus && (
            <Form.Item name="orderStatus">
              <CustomSelect
                placeholder={placeHolders.filterStatus}
                style={{ width: '100px' }}
                options={orderStatus}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.providers && (
            <Form.Item name="providersIds">
              <CustomSelect
                mode="multiple"
                placeholder={placeHolders.filterProviders}
                style={{ width: '150px' }}
                options={providers?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.paymentMethod && (
            <Form.Item name="paymentMethod">
              <CustomSelect
                placeholder={placeHolders.filterPaymentMethod}
                style={{ width: '150px' }}
                options={paymentMethodOptions}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.paymentGateway && (
            <Form.Item name="paymentGateway">
              <CustomSelect
                placeholder={paymentGatewayPlaceholder}
                style={{ width: '150px' }}
                options={gatewayOptions}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.paymentStatus && (
            <Form.Item name="paymentStatus">
              <CustomSelect
                placeholder={placeHolders.paymentStatus}
                style={{ width: '150px' }}
                options={paymentStatusOptions}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.treatmentStatus && (
            <Form.Item name="treatmentStatus">
              <CustomSelect
                placeholder={placeHolders.treatmentStatus}
                style={{ width: '190px' }}
                options={treatmentStatusOptions}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.redemptionBranch && (
            <Form.Item name="redemptionBranchId">
              <CustomSelect
                placeholder={placeHolders.redemptionBranch}
                style={{ minWidth: '190px' }}
                options={subsidiaries?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.payMethods && (
            <Form.Item name="payMethod">
              <CustomSelect
                placeholder={placeHolders.filterPaymentMethod}
                style={{ width: '150px' }}
                options={payMethods}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.payStatus && (
            <Form.Item name="payStatus">
              <CustomSelect
                placeholder={placeHolders.paymentStatus}
                style={{ width: '150px' }}
                options={payStatus}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.payConditions && (
            <Form.Item name="payCondition">
              <CustomSelect
                multiple
                placeholder={placeHolders.filterPayConditions}
                style={{ width: '150px' }}
                options={payConditionsConst}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.categoryType && (
            <Form.Item name="type">
              <CustomSelect
                multiple
                placeholder={placeHolders.filterType}
                style={{ width: '150px' }}
                options={categoryTypeConst}
              ></CustomSelect>
            </Form.Item>
          )}

          {filters.search && (
            <Form.Item
              hidden={[roleConst.medic, roleConst.cosmetologist].includes(role)}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  width: '300px',
                }}
              >
                <Form.Item name={['search', 'filter']} noStyle>
                  <CustomSelect
                    placeholder={placeHolders.searchOption}
                    style={{ width: '150px' }}
                    options={searchFilters.map(({ value, label }) => ({
                      value,
                      label,
                    }))}
                  ></CustomSelect>
                </Form.Item>

                <Form.Item name={['search', 'value']} noStyle>
                  <Input
                    style={{ width: '150px' }}
                    placeholder={placeHolders.search}
                    allowClear
                  />
                </Form.Item>
              </div>
            </Form.Item>
          )}

          <Form.Item>
            <Button type="primary" className="btn-form" htmlType="submit">
              {buttonLabel.search}
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              type="ghost"
              className="btn-form"
              onClick={handleClearFilters}
            >
              {buttonLabel.clear}
            </Button>
          </Form.Item>
        </Space>
      </Form>
    </Row>
  );
};
